import { useState, type FormEvent } from 'react';
import { contactChannels } from '@/mocks/home';

const SUBMIT_ADDR = 'https://readdy.ai/api/form/dahhlkjoh653ivfvnvq0';
const MESSAGE_LIMIT = 500;

type Status = 'idle' | 'submitting' | 'success' | 'error';

export default function ContactSection() {
  const [status, setStatus] = useState<Status>('idle');
  const [formError, setFormError] = useState('');
  const [messageLen, setMessageLen] = useState(0);

  const handleSubmit = async (event: FormEvent<HTMLFormElement>) => {
    event.preventDefault();
    const formEl = event.currentTarget;
    const formData = new FormData(formEl);

    const guard = String(formData.get('contact_alt') ?? '').trim();
    if (guard) {
      setStatus('success');
      setFormError('');
      formEl.reset();
      setMessageLen(0);
      return;
    }

    const params = new URLSearchParams();
    formData.forEach((value, key) => {
      if (key === 'contact_alt') return;
      if (typeof value === 'string' && value.trim() !== '') {
        params.append(key, value);
      }
    });

    setStatus('submitting');
    setFormError('');

    try {
      const response = await fetch(SUBMIT_ADDR, {
        method: 'POST',
        headers: { 'Content-Type': 'application/x-www-form-urlencoded' },
        body: params.toString(),
      });

      const responseText = await response.text();
      let parsed: {
        code?: string;
        message?: string;
        meta?: { message?: string; detail?: string };
      } | null = null;
      try {
        parsed = responseText ? JSON.parse(responseText) : null;
      } catch {
        parsed = null;
      }

      const code = parsed?.code ?? '';
      const serverMsg =
        parsed?.meta?.message || parsed?.message || parsed?.meta?.detail || responseText || '';
      const isSpam = String(serverMsg).toLowerCase().includes('spam');

      if (response.ok && code === 'OK' && !isSpam) {
        setStatus('success');
        formEl.reset();
        setMessageLen(0);
      } else {
        setStatus('error');
        setFormError(
          typeof serverMsg === 'string' && serverMsg.trim() !== ''
            ? serverMsg
            : '提交失败，请稍后重试，或直接发送邮件给我们。',
        );
      }
    } catch {
      setStatus('error');
      setFormError('网络连接异常，请检查网络后重试，或直接发送邮件给我们。');
    }
  };

  return (
    <section id="contact" className="bg-background-100 paper-grain py-14 md:py-20">
      <div className="mx-auto max-w-8xl px-4 md:px-8">
        <div className="grid grid-cols-1 gap-10 lg:grid-cols-12 lg:gap-14">
          <div className="lg:col-span-5">
            <span className="inline-flex items-center gap-2 text-xs tracking-[0.2em] text-primary-600">
              <i className="ri-send-plane-line"></i>
              CONTACT
            </span>
            <h2 className="mt-3 font-heading text-2xl font-bold text-foreground-950 md:text-4xl">
              投稿线索 · 订阅更新
            </h2>
            <p className="mt-4 text-sm leading-relaxed text-foreground-600 md:text-base">
              如果你手上有可核验的公开材料，或者希望第一时间收到深度观察更新，欢迎在这里留下信息。我们只处理与内容相关的事项。
            </p>

            <ul className="mt-7 space-y-4">
              {contactChannels.map((channel) => (
                <li
                  key={channel.id}
                  className="flex items-start gap-4 rounded-lg border border-background-300/70 bg-background-50 p-4"
                >
                  <span className="flex h-11 w-11 shrink-0 items-center justify-center rounded-md bg-accent-100 text-xl text-accent-800">
                    <i className={channel.icon}></i>
                  </span>
                  <span className="min-w-0">
                    <span className="block text-xs text-foreground-500">{channel.label}</span>
                    <span className="mt-1 block break-all text-sm font-medium text-foreground-900">
                      {channel.value}
                    </span>
                    <span className="mt-1 block text-xs leading-relaxed text-foreground-500">
                      {channel.desc}
                    </span>
                  </span>
                </li>
              ))}
            </ul>
          </div>

          <div className="lg:col-span-7">
            <div className="rounded-lg border border-background-300/70 bg-background-50 p-5 md:p-8">
              <form
                id="form-heiliao-tips"
                data-readdy-form
                onSubmit={handleSubmit}
                noValidate={false}
                className="space-y-5"
              >
                <div>
                  <label
                    htmlFor="email"
                    className="mb-2 block text-sm font-medium text-foreground-800"
                  >
                    联系邮箱
                  </label>
                  <input
                    id="email"
                    name="email"
                    type="email"
                    required
                    autoComplete="email"
                    placeholder="name@example.com"
                    className="w-full rounded-md border border-background-300 bg-background-50 px-4 py-3 text-sm text-foreground-900 outline-none transition-colors placeholder:text-foreground-400 focus:border-primary-400 focus:ring-2 focus:ring-primary-400/25"
                  />
                </div>

                <div>
                  <label
                    htmlFor="topic"
                    className="mb-2 block text-sm font-medium text-foreground-800"
                  >
                    事项类型
                  </label>
                  <select
                    id="topic"
                    name="topic"
                    required
                    defaultValue=""
                    className="w-full appearance-none rounded-md border border-background-300 bg-background-50 px-4 py-3 text-sm text-foreground-900 outline-none transition-colors focus:border-primary-400 focus:ring-2 focus:ring-primary-400/25"
                  >
                    <option value="" disabled>
                      请选择事项类型
                    </option>
                    <option value="行业线索投稿">行业线索投稿</option>
                    <option value="内容更正说明">内容更正说明</option>
                    <option value="订阅深度更新">订阅深度更新</option>
                    <option value="内容合作咨询">内容合作咨询</option>
                  </select>
                </div>

                <div>
                  <div className="mb-2 flex items-center justify-between">
                    <label htmlFor="message" className="text-sm font-medium text-foreground-800">
                      留言内容
                    </label>
                    <span className="text-xs text-foreground-400">
                      {messageLen} / {MESSAGE_LIMIT}
                    </span>
                  </div>
                  <textarea
                    id="message"
                    name="message"
                    required
                    rows={5}
                    maxLength={MESSAGE_LIMIT}
                    onChange={(event) => setMessageLen(event.target.value.length)}
                    placeholder="请简要说明线索来源、可核验材料，或你希望订阅的内容方向。"
                    className="w-full resize-y rounded-md border border-background-300 bg-background-50 px-4 py-3 text-sm leading-relaxed text-foreground-900 outline-none transition-colors placeholder:text-foreground-400 focus:border-primary-400 focus:ring-2 focus:ring-primary-400/25"
                  ></textarea>
                </div>

                <div className="field-guard-ra9" aria-hidden="true">
                  <label htmlFor="contact_alt">请勿填写此项</label>
                  <input
                    id="contact_alt"
                    name="contact_alt"
                    type="text"
                    tabIndex={-1}
                    autoComplete="off"
                    readOnly
                  />
                </div>

                <button
                  type="submit"
                  disabled={status === 'submitting'}
                  className="inline-flex w-full items-center justify-center gap-2 rounded-md bg-primary-500 px-6 py-3.5 text-sm font-medium text-background-50 whitespace-nowrap transition-colors hover:bg-primary-600 disabled:cursor-not-allowed disabled:opacity-60 sm:w-auto"
                >
                  {status === 'submitting' ? (
                    <>
                      <i className="ri-loader-4-line animate-spin"></i>
                      提交中…
                    </>
                  ) : (
                    <>
                      <i className="ri-send-plane-fill"></i>
                      提交信息
                    </>
                  )}
                </button>

                {status === 'success' && (
                  <p
                    role="status"
                    className="flex items-start gap-3 rounded-md border border-accent-300 bg-accent-50 px-4 py-3 text-sm text-accent-900"
                  >
                    <i className="ri-checkbox-circle-line mt-0.5 text-lg"></i>
                    已收到你的信息，我们会在 2 个工作日内查看处理。
                  </p>
                )}
                {status === 'error' && (
                  <p
                    role="alert"
                    className="flex items-start gap-3 rounded-md border border-primary-300 bg-primary-50 px-4 py-3 text-sm text-primary-900"
                  >
                    <i className="ri-error-warning-line mt-0.5 text-lg"></i>
                    {formError}
                  </p>
                )}

                <p className="text-xs leading-relaxed text-foreground-400">
                  提交即表示你已阅读并同意我们仅将上述信息用于内容核验与更新通知，不会对外公开你的联系方式。
                </p>
              </form>
            </div>
          </div>
        </div>
      </div>
    </section>
  );
}